import axios from "axios";
import { clearLocalStorage, notifyError } from "../utility";

const isProduction = () => {
  return process.env.NODE_ENV === 'production';
};

let API_BASE_URL = process.env.REACT_APP_API_BASE_URL || '';

if (isProduction()) {
  if (API_BASE_URL) {
    console.error('❌ ERROR: REACT_APP_API_BASE_URL should be empty in production!');
    console.error('   Production must use service-specific URLs only.');
    console.error('   Check webpack.config.js - REACT_APP_API_BASE_URL should be forced to empty string.');
    API_BASE_URL = '';
  }
}

export const BASE_URL = API_BASE_URL;

/**
 * Gets the current language from storage
 * @returns {string} The current language ('en' or 'ar')
 */
const getCurrentLanguage = () => {
  try {
    const language = localStorage.getItem('app_language');
    return language || 'en';
  } catch (error) {
    console.warn('Error retrieving language from storage:', error);
    return 'en';
  }
};

/**
 * Creates request interceptor
 * @returns {Function} Request interceptor function
 */
const createRequestInterceptor = () => {
  return (config) => {
    if (!config.headers) {
      config.headers = {};
    }

    if (!config.headers["Content-Type"]) {
      if (config.data instanceof FormData) {
        delete config.headers["Content-Type"];
      } else {
        config.headers["Content-Type"] = "application/json";
      }
    }

    const currentLanguage = getCurrentLanguage();
    config.headers["Accept-Language"] = currentLanguage;
    config.baseURL = '';
    return config;
  };
};
/**
 * Creates error interceptor
 * @returns {Function} Error interceptor function
 */
const createErrorInterceptor = () => {
  return (error) => {
    const originalRequest = error?.config;

    // Handle CORS/network errors
    if (!error.response) {
      const corsError = {
        data: {
          status: false,
          message: 'Network error: Unable to connect to server. Please check your connection.',
        },
        status: 0,
        isCorsError: true,
      };
      return Promise.reject(corsError);
    }

    const status = error.response.status;

    // Handle 401 Unauthorized -> Token invalid or missing
    if (status === 401 && !originalRequest?._retry) {
      originalRequest._retry = true;
      sessionStorage.clear();
      clearLocalStorage();
      window.location.href = "/login";
      return Promise.reject(error.response);
    }

    // Handle 403 Access Denied -> IP blocked or insufficient permissions or Cloudflare challenge
    if (status === 403) {
      const responseData = typeof error.response.data === 'string' ? error.response.data : '';
      const isChallengeOrUnauthorized =
        responseData.includes('<html') ||
        responseData.includes('<!DOCTYPE') ||
        responseData.includes('Just a moment') ||
        responseData.includes('cf_chl_opt');

      if (isChallengeOrUnauthorized) {
        sessionStorage.clear();
        clearLocalStorage();
        window.location.href = "/login";
        return Promise.reject(error.response);
      }

      console.error("Access Forbidden: Potential IP Block.", error.response);
      notifyError("Access Forbidden: Your request was blocked. Please contact support.");
      return Promise.reject(error.response);
    }

    return Promise.reject(error.response);
  };
};

/**
 * Creates a configured axios instance
 * @returns {axios.AxiosInstance}
 */
export const createAxiosInstance = () => {

  const instance = axios.create({
    baseURL: '',
    timeout: 30000,
    withCredentials: true,
  });

  instance.interceptors.request.use(
    createRequestInterceptor(),
    (error) => Promise.reject(error)
  );

  instance.interceptors.response.use(
    (response) => {
      const contentType = response?.headers?.['content-type'] || response?.headers?.['Content-Type'] || '';
      const isHtml =
        typeof response?.data === 'string' &&
        (
          (typeof contentType === 'string' && contentType.toLowerCase().includes('text/html')) ||
          response.data.includes('<html') ||
          response.data.includes('<!DOCTYPE') ||
          response.data.includes('Just a moment') ||
          response.data.includes('cf_chl_opt')
        );

      if (isHtml) {
        sessionStorage.clear();
        clearLocalStorage();
        window.location.href = "/login";
        return Promise.reject({
          response: {
            status: 401,
            data: {
              status: false,
              message:
                'API request returned HTML instead of JSON. Redirecting to login...',
              isMisroutedHtml: true,
            },
          },
          config: response?.config,
        });
      }
      return response;
    },
    createErrorInterceptor()
  );

  return instance;
};

const ApiCaller = createAxiosInstance();

export { ApiCaller, ApiCaller as AuthApiCaller };
export default ApiCaller;
